import { useState } from "react";
import { createConference, joinConference } from "../../../functions/call";
import { ConferenceIcon, VideoIcon, PhoneIcon } from "../../../svg/chat";
import "./style.css";

export default function CreateConference({ user, onClose, onConferenceStart }) {
  const [activeTab, setActiveTab] = useState("create");
  const [title, setTitle] = useState("");
  const [type, setType] = useState("video");
  const [joinCode, setJoinCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Settings
  const [muteOnJoin, setMuteOnJoin] = useState(false);
  const [videoOffOnJoin, setVideoOffOnJoin] = useState(false);
  const [waitingRoom, setWaitingRoom] = useState(false);
  const [allowScreenShare, setAllowScreenShare] = useState(true);

  const handleCreate = async () => {
    setLoading(true);
    setError("");

    const settings = {
      muteOnJoin,
      videoOffOnJoin,
      waitingRoom,
      allowScreenShare,
    };

    const result = await createConference(title || "Minha Conferência", type, settings, user.token);

    if (result.status === "ok") {
      onConferenceStart(result.conference);
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) {
      setError("Digite o código da conferência");
      return;
    }

    setLoading(true);
    setError("");

    const result = await joinConference(joinCode.trim().toUpperCase(), user.token);

    if (result.status === "ok") {
      if (result.inWaitingRoom) {
        setError("Você foi adicionado à sala de espera. Aguarde a aprovação do organizador.");
        setLoading(false);
        return;
      }
      onConferenceStart(result.conference);
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  const formatCode = (value) => {
    // Remove non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

    // Add dashes every 3 characters
    let formatted = "";
    for (let i = 0; i < cleaned.length && i < 9; i++) {
      if (i > 0 && i % 3 === 0) formatted += "-";
      formatted += cleaned[i];
    }

    return formatted;
  };

  return (
    <div className="create_conference_overlay" onClick={onClose}>
      <div className="create_conference_modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal_header">
          <h2>
            <ConferenceIcon color="var(--color-primary)" />
            Conferência
          </h2>
          <button className="close_btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="modal_tabs">
          <button
            className={`tab ${activeTab === "create" ? "active" : ""}`}
            onClick={() => setActiveTab("create")}
          >
            Criar
          </button>
          <button
            className={`tab ${activeTab === "join" ? "active" : ""}`}
            onClick={() => setActiveTab("join")}
          >
            Entrar
          </button>
        </div>

        <div className="modal_content">
          {/* Create Tab */}
          {activeTab === "create" && (
            <div className="create_form">
              <div className="form_group">
                <label>Nome da conferência</label>
                <input
                  type="text"
                  placeholder="Ex: Reunião de equipe"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={50}
                />
              </div>

              <div className="form_group">
                <label>Tipo</label>
                <div className="type_selector">
                  <button
                    className={`type_btn ${type === "video" ? "active" : ""}`}
                    onClick={() => setType("video")}
                  >
                    <VideoIcon color={type === "video" ? "#fff" : "var(--color-primary)"} />
                    Vídeo
                  </button>
                  <button
                    className={`type_btn ${type === "voice" ? "active" : ""}`}
                    onClick={() => setType("voice")}
                  >
                    <PhoneIcon color={type === "voice" ? "#fff" : "var(--color-primary)"} />
                    Voz
                  </button>
                </div>
              </div>

              <div className="form_group settings_group">
                <label>Configurações</label>
                <div className="settings_list">
                  <label className="setting_item">
                    <input
                      type="checkbox"
                      checked={waitingRoom}
                      onChange={(e) => setWaitingRoom(e.target.checked)}
                    />
                    <span className="setting_info">
                      <span className="setting_name">Sala de espera</span>
                      <span className="setting_desc">Aprovar participantes antes de entrar</span>
                    </span>
                  </label>

                  <label className="setting_item">
                    <input
                      type="checkbox"
                      checked={muteOnJoin}
                      onChange={(e) => setMuteOnJoin(e.target.checked)}
                    />
                    <span className="setting_info">
                      <span className="setting_name">Mudo ao entrar</span>
                      <span className="setting_desc">Participantes entram com mic desligado</span>
                    </span>
                  </label>

                  {type === "video" && (
                    <label className="setting_item">
                      <input
                        type="checkbox"
                        checked={videoOffOnJoin}
                        onChange={(e) => setVideoOffOnJoin(e.target.checked)}
                      />
                      <span className="setting_info">
                        <span className="setting_name">Vídeo desligado ao entrar</span>
                        <span className="setting_desc">Participantes entram com câmera desligada</span>
                      </span>
                    </label>
                  )}

                  <label className="setting_item">
                    <input
                      type="checkbox"
                      checked={allowScreenShare}
                      onChange={(e) => setAllowScreenShare(e.target.checked)}
                    />
                    <span className="setting_info">
                      <span className="setting_name">Permitir compartilhar tela</span>
                      <span className="setting_desc">Participantes podem compartilhar tela</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Join Tab */}
          {activeTab === "join" && (
            <div className="join_form">
              <div className="join_illustration">
                <ConferenceIcon color="var(--blue-color)" />
              </div>
              <p className="join_description">
                Digite o código da conferência para entrar
              </p>
              <div className="form_group">
                <input
                  type="text"
                  placeholder="XXX-XXX-XXX"
                  value={joinCode}
                  onChange={(e) => setJoinCode(formatCode(e.target.value))}
                  className="join_code_input"
                  maxLength={11}
                />
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="error_message">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {error}
            </div>
          )}
        </div>

        <div className="modal_footer">
          <button className="cancel_btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="primary_btn"
            onClick={activeTab === "create" ? handleCreate : handleJoin}
            disabled={loading}
          >
            {loading ? (
              <span className="loading_spinner"></span>
            ) : activeTab === "create" ? (
              "Iniciar Conferência"
            ) : (
              "Entrar"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
